import axios from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  DashboardStats, 
  UsersResponse, 
  UserDetails, 
  UserDetailsResponse,
  User,
  CreateUserRequest, 
  UpdateUserRequest, 
  ResetPasswordRequest,
  OpenAIErrorsResponse,
  OpenAIErrorStatistics,
  UserConversationsResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sergic-backend-7526fb1dc158.herokuapp.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const adminAPI = {
  // Auth
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/admin/login', credentials);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (period: string = '30d'): Promise<DashboardStats> => {
    const response = await api.get(`/admin/dashboard/stats?period=${period}`);
    return response.data;
  },

  // Users
  getUsers: async (page: number = 1, limit: number = 20, search: string = ''): Promise<UsersResponse> => {
    const response = await api.get(`/admin/users?page=${page}&limit=${limit}&search=${search}`);
    return response.data;
  },

  getUserDetails: async (userId: number): Promise<UserDetailsResponse> => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  createUser: async (userData: CreateUserRequest): Promise<{ message: string; user: User }> => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  updateUser: async (userId: number, userData: UpdateUserRequest): Promise<{ message: string; user: User }> => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  resetUserPassword: async (userId: number, passwordData: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await api.put(`/admin/users/${userId}/reset-password`, passwordData);
    return response.data;
  },

  // Logs
  getSystemLogs: async (level: string = 'all', limit: number = 100, page: number = 1): Promise<{ logs: any[]; pagination: any }> => {
    const response = await api.get(`/admin/logs?level=${level}&limit=${limit}&page=${page}`);
    return response.data;
  },

  getLogStatistics: async (): Promise<any> => {
    const response = await api.get('/admin/logs/statistics');
    return response.data;
  },

  // OpenAI Errors
  getOpenAIErrors: async (
    page: number = 1, 
    limit: number = 20, 
    errorType: string = '', 
    userId: string = '', 
    startDate: string = '', 
    endDate: string = ''
  ): Promise<OpenAIErrorsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (errorType) params.append('errorType', errorType);
    if (userId) params.append('userId', userId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/openai-errors?${params.toString()}`);
    return response.data;
  },

  getOpenAIErrorStatistics: async (days: number = 7): Promise<OpenAIErrorStatistics> => {
    const response = await api.get(`/admin/openai-errors/statistics?days=${days}`);
    return response.data;
  },

  // User Conversations
  getUserConversations: async (userId: number, page: number = 1, limit: number = 20): Promise<UserConversationsResponse> => {
    const response = await api.get(`/admin/users/${userId}/conversations?page=${page}&limit=${limit}`);
    return response.data;
  },
};

export default api;
